import { ArrowRight, Smile, Heart, Bone, Baby, Eye, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SmoothLink as Link } from "@/components/navigation";

const treatments = [
  {
    icon: Smile,
    title: "Dental Care",
    slug: "dental",
    description: "Implants, veneers, smile makeover & full mouth rehabilitation",
    savings: "Up to 70% savings",
    popular: true,
    procedures: ["Dental Implants", "All-on-4", "Veneers", "Root Canal"],
  },
  {
    icon: Heart,
    title: "Cardiac Surgery",
    slug: "cardiac",
    description: "Heart bypass, valve replacement & advanced cardiac care",
    savings: "Up to 80% savings",
    popular: false,
    procedures: ["Bypass Surgery", "Valve Replacement", "Angioplasty"],
  },
  {
    icon: Bone,
    title: "Orthopedics",
    slug: "orthopedics",
    description: "Joint replacement, spine surgery & sports medicine",
    savings: "Up to 75% savings",
    popular: false,
    procedures: ["Hip Replacement", "Knee Replacement", "Spine Surgery"],
  },
  {
    icon: Eye,
    title: "Ophthalmology",
    slug: "ophthalmology",
    description: "LASIK, cataract surgery & advanced eye treatments",
    savings: "Up to 65% savings",
    popular: false,
    procedures: ["LASIK", "Cataract Surgery", "Glaucoma Treatment"],
  },
  {
    icon: Baby,
    title: "Fertility",
    slug: "fertility",
    description: "IVF, IUI & comprehensive fertility treatments",
    savings: "Up to 70% savings",
    popular: false,
    procedures: ["IVF", "IUI", "Egg Freezing", "Surrogacy"],
  },
  {
    icon: Stethoscope,
    title: "Health Checkups",
    slug: "wellness", // Mapping to closest valid category
    description: "Comprehensive wellness packages & preventive care",
    savings: "Up to 60% savings",
    popular: false,
    procedures: ["Executive Checkup", "Cardiac Screening", "Cancer Screening"],
  },
];

export function TreatmentsSection() {
  return (
    <section id="treatments" className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
          <span className="inline-block text-primary font-medium text-sm uppercase tracking-wider mb-4">
            Our Specialities
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-secondary mb-4">
            World-Class Treatments
          </h2>
          <p className="text-foreground/70 text-lg">
            Access India's top hospitals and doctors at a fraction of Western costs. All treatments include concierge support.
          </p>
        </div>

        {/* Treatment Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {treatments.map((treatment) => (
            <Link
              key={treatment.title}
              to={`/treatments/${treatment.slug}/`}
              className="group relative bg-card rounded-lg p-6 lg:p-8 shadow-soft hover:shadow-hover transition-all duration-300 hover:-translate-y-1 block"
            >
                {/* Popular Badge */}
                {treatment.popular && (
                  <div className="absolute -top-3 right-6 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}

                {/* Icon */}
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <treatment.icon className="w-7 h-7 text-primary" />
                </div>

                {/* Content */}
                <h3 className="font-display text-xl font-semibold text-secondary mb-2">
                  {treatment.title}
                </h3>
                <p className="text-foreground/70 text-sm mb-4">
                  {treatment.description}
                </p>

                {/* Procedures List */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {treatment.procedures.map((procedure) => (
                    <span
                      key={procedure}
                      className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-md"
                    >
                      {procedure}
                    </span>
                  ))}
                </div>

                {/* Savings Badge */}
                <div className="flex items-center justify-between">
                  <span className="text-accent font-semibold text-sm">
                    {treatment.savings}
                  </span>
                  <div className="flex items-center text-primary font-medium text-sm gap-1 group-hover:gap-2 transition-all">
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button asChild variant="default" className="rounded-full px-8 font-medium">
            <Link to="/treatments/">
              View All Treatments
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
